// @ts-nocheck
import React, { useState, useEffect, useLayoutEffect, useRef, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'gatsby';
import { useLocation } from '@gatsbyjs/reach-router';
import { ASSETS, flagSvg, venueLogo, industryIcon } from './assets';
import { LEADERS, PETS } from './teamData';
import { FEATURED_CASE_STUDY, MORE_CASE_STUDIES } from './caseStudiesData';
import { LANGS, PhoneTour, EditorMock, AnalyticsMock, QRCode } from './mockui';
import { BOOK_DEMO_HREF, isBookDemoTrigger, shouldOpenDemoModalFromHash } from './bookDemo';

const FOOTER_LINKS = {
  Pricing: '/pricing/',
  Contact: '/contact/',
  Team: '/team/',
  'Case studies': '/case-studies/',
};

const isValidNavHref = (href) => {
  if (!href || href === '#') return false;
  if (href.startsWith('#') || href.startsWith('/#')) return true;
  if (/^https?:\/\//.test(href)) return true;
  if (href.startsWith('/') && !/\.html?(\?|#|$)/i.test(href)) return true;
  return false;
};

const resolveNavItemHref = (item, sectionFallback) => {
  if (item.soon) return null;
  if (item.href) return isValidNavHref(item.href) ? item.href : null;
  return isValidNavHref(sectionFallback) ? sectionFallback : null;
};

const preventUnavailableNav = (e) => {
  e.preventDefault();
};

function NavMenuIcon({ icon, iconSrc }) {
  if (iconSrc) return <img src={iconSrc} alt="" />;
  return icon;
}

// ====== Announcement bar ======
function Announce() {
  return (
    <div className="announce">
      <div className="inner">
        <strong>New</strong>
        <span>How Jacksonville Zoo increased engagement with multilingual experiences.</span>
        <Link to="/case-studies/">Read the success stories →</Link>
      </div>
    </div>);

}

// ====== Nav ======
function Nav() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerExpanded, setDrawerExpanded] = useState(null);
  const closeTimer = useRef(null);
  const mobileCloseReason = useRef<'toggle' | 'route'>('toggle');
  const [mounted, setMounted] = useState(false);
  const path = (location.pathname || '/').replace(/\/$/, '') || '/';
  const homeHref = (hash) => (path === '/' ? hash : `/${hash}`);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!mobileOpen) return;
    document.body.classList.add('mobile-nav-open');
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.classList.remove('mobile-nav-open');
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (mobileCloseReason.current === 'toggle') {
        window.scrollTo(0, scrollY);
      }
      mobileCloseReason.current = 'toggle';
    };
  }, [mobileOpen]);

  useLayoutEffect(() => {
    mobileCloseReason.current = 'route';
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setMobileOpen(false);
    setOpenMenu(null);
    setDrawerExpanded(null);
  }, [location.pathname]);

  useEffect(() => {
    const sync = () => setScrolled(window.scrollY > 8);
    sync();
    const onScroll = () => sync();
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) sync();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pageshow', onPageShow);
    const raf = requestAnimationFrame(sync);
    const timer = window.setTimeout(sync, 100);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pageshow', onPageShow);
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
  }, [location.pathname]);
  const openMenuFor = (id) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenMenu(id);
  };
  const closeMenu = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMenu(null), 120);
  };
  const Caret = () =>
  <svg className="caret" viewBox="0 0 12 12" fill="none">
      <path d="M3 5L6 8L9 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>;

  const features = [
  { label: 'Audio Tours', icon: '🎧' },
  { label: 'Self-Guided Multilingual Tours', icon: '🗺' },
  { label: 'Branded QR Codes', icon: '▣' },
  { label: 'Surveys', icon: '📝' },
  { label: 'Donations', icon: '💝' },
  { label: 'Game Design', icon: '🎮' },
  { label: 'AI Content Creation', icon: '✨' },
  { label: 'Event Ticketing', icon: '🎟', soon: true }];

  const solutions = [
  { label: 'Amusement Parks', iconSrc: industryIcon('amusement-parks') },
  { label: 'Aquariums', iconSrc: industryIcon('aquariums') },
  { label: 'Botanical Gardens', iconSrc: industryIcon('botanical-gardens') },
  { label: 'City Parks & Downtowns', iconSrc: industryIcon('city-parks-downtowns') },
  { label: 'Museums & Historic Sites', iconSrc: industryIcon('museums-historic-sites') },
  { label: 'Zoos', iconSrc: industryIcon('zoos') },
  { label: 'Other Industries', iconSrc: industryIcon('other-industries') }];

  return (
    <nav className={"nav" + (scrolled ? " scrolled" : "")} data-screen-label="Nav">
      <div className="inner">
        <Link
          to="/"
          className="brand"
          aria-label="CheQRboard home"
          onClick={(e) => {
            if (path === '/') {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}>
          <img className="logo-full" src={ASSETS.logoPrimary} alt="CheQRboard" />
          <img className="logo-mark" src={ASSETS.logoMobile} alt="CheQRboard" />
        </Link>
        <ul>
          {/* Hidden — spacer placeholders preserve desktop nav gap */}
          <li className="nav-item-hidden" aria-hidden="true">
            <span className="nav-item-placeholder">Solutions<Caret /></span>
          </li>
          <li className="nav-item-hidden" aria-hidden="true">
            <span className="nav-item-placeholder">Features<Caret /></span>
          </li>
          <li><Link to="/case-studies/">Case Studies</Link></li>
          <li><Link to="/pricing/">Pricing</Link></li>
          <li><Link to="/team/">Team</Link></li>
          <li><Link to="/contact/">Contact Us</Link></li>
        </ul>
        <div className="spacer" />
        <div className="auth">
          <a href="https://app.cheqrboard.com/app/login" className="btn btn-ghost">Sign in</a>
          <a href={BOOK_DEMO_HREF} className="btn btn-primary" data-book-demo>Book a demo<span className="arrow">→</span></a>
        </div>

        {/* Mobile actions: compact Book a demo + hamburger */}
        <div className="mobile-actions">
          <a href={BOOK_DEMO_HREF} className="btn btn-primary mobile-demo" data-book-demo>Demo</a>
          <button
            className={"hamburger" + (mobileOpen ? " open" : "")}
            aria-label="Open menu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}>
            <span></span><span></span><span></span>
          </button>
        </div>
      </div>

      {mounted && createPortal(
        <div className={"mobile-drawer" + (mobileOpen ? " open" : "")}>
          <button className="mobile-drawer-scrim" aria-label="Close menu" onClick={() => setMobileOpen(false)} />
          <div className="mobile-drawer-panel">
            <button className="mobile-drawer-close" aria-label="Close menu" onClick={() => setMobileOpen(false)}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M4 4l12 12M16 4L4 16" />
              </svg>
            </button>
            <div className="mobile-drawer-links">
              <Link className="md-link" to="/case-studies/" onClick={() => setMobileOpen(false)}>Case Studies</Link>
              <Link className="md-link" to="/pricing/" onClick={() => setMobileOpen(false)}>Pricing</Link>
              <Link className="md-link" to="/team/" onClick={() => setMobileOpen(false)}>Team</Link>
              <Link className="md-link" to="/contact/" onClick={() => setMobileOpen(false)}>Contact Us</Link>
            </div>
            <div className="mobile-drawer-foot">
              <a href="https://app.cheqrboard.com/app/login" className="btn btn-ghost">Sign in</a>
              <a href={BOOK_DEMO_HREF} className="btn btn-primary" data-book-demo onClick={() => setMobileOpen(false)}>Book a demo<span className="arrow">→</span></a>
            </div>
          </div>
        </div>,
        document.body
      )}
    </nav>);

}

// ====== Hero ======
function Hero() {
  const [langIdx, setLangIdx] = useState(0);
  const [locked, setLocked] = useState(false);
  useEffect(() => {
    if (locked) return;
    // Mobile shows only 5 pills — cycle through just those languages.
    const isMobile = window.matchMedia('(max-width: 640px)').matches;
    const seq = isMobile ? [0, 2, 4, 6, 1] : null;
    const id = setInterval(() => setLangIdx((i) => {
      if (seq) {
        const pos = seq.indexOf(i);
        return seq[(pos + 1) % seq.length];
      }
      return (i + 1) % LANGS.length;
    }), 4000);
    return () => clearInterval(id);
  }, [locked]);
  const lang = LANGS[langIdx];

  const handleSelect = (pillCode) => {
    const idx = LANGS.findIndex((l) => l.name === pillCode || l.code === pillCode);
    if (idx >= 0) {
      setLangIdx(idx);
      setLocked(true);
    }
  };

  return (
    <section className="hero" data-screen-label="Hero">
      <div className="wrap">
        <div className="grid">
          <div className="hero-copy">
            <h1>
              Right Message,<br />
              Right Guest,<br />
              <span className="cyan">Right Time.</span>
            </h1>
            <p className="lede">
              CheQRboard turns static signage into interactive guest experiences for aquariums, gardens, museums, zoos, and more. Create visitor surveys, self-guided tours using QR codes, fun games, and donations prompts in minutes, and deliver them in <strong>133+ languages</strong>.
            </p>
            <div className="ctas">
              <a href={BOOK_DEMO_HREF} className="btn btn-primary" data-book-demo>Book a demo<span className="arrow">→</span></a>
            </div>
            <div className="micro" style={{ marginTop: 'auto', paddingTop: 40, position: 'relative', top: 0 }}>
              <span><span className="check">✓</span> No app download for guests</span>
              <span><span className="check">✓</span> 5-minute setup, zero hardware</span>
              <span className="micro-analytics"><span className="check">✓</span> Enhanced analytics</span>
            </div>
          </div>
          <div>
            <HeroScene langIdx={langIdx} lang={lang} onSelect={handleSelect} />
          </div>
        </div>
      </div>
    </section>);

}

// ====== Hero Scene — phone center, language pills flanking, QR + insight callouts ======
function HeroScene({ langIdx, lang, onSelect }) {
  // Left column pills and right column pills — positioned OUTSIDE the phone
  // Pills sit on a gentle convex arc around the phone — middle pills bulge
  // outward toward the screen edges, top/bottom pills tuck in toward the device.
  const leftPills = [
  { code: 'English', flagIcon: flagSvg('us'), top: 150, dx: 30 },
  { code: 'Français', flagIcon: flagSvg('fr'), top: 268, dx: 2 },
  { code: 'Українська', flagIcon: flagSvg('ua'), top: 386, dx: 2 },
  { code: 'العربية', flagIcon: flagSvg('sa'), top: 504, dx: 30 }];

  const rightPills = [
  { code: 'Español', flagIcon: flagSvg('es'), top: 150, dx: 30 },
  { code: 'Deutsch', flagIcon: flagSvg('de'), top: 268, dx: 2 },
  { code: 'فارسی', flagIcon: flagSvg('ir'), top: 386, dx: 2 },
  { code: '日本語', flagIcon: flagSvg('jp'), top: 504, dx: 30 }];

  return (
    <div className="scene" style={{ height: 620, maxWidth: 580 }}>
      {/* sweeping glow */}
      <div style={{
        position: 'absolute', inset: '8% -8% 8% -8%',
        background: 'radial-gradient(ellipse at center, rgba(31,213,244,.18) 0%, transparent 60%)',
        pointerEvents: 'none'
      }} />

      {/* QR badge top-left (peeks above phone) */}
      <div className="hero-qr-badge" style={{
        position: 'absolute', top: -24, left: -8, zIndex: 4,
        background: '#eef0f5', borderRadius: 16, padding: '8px 12px',
        border: '2px solid #1FD5F4',
        display: 'flex', alignItems: 'center', gap: 11,
        maxWidth: 228,
        animation: 'qrBadgeGlow 2.4s ease-in-out infinite'
      }}>
        <img src={ASSETS.lionQr} alt="Scan at Lion Loop" style={{ width: 48, height: 48, display: 'block', borderRadius: 5 }} />
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 12.5, color: '#0B1A3F', letterSpacing: '-0.01em', lineHeight: 1.2 }}>Select your language and click next for trivia!</div>
        </div>
      </div>

      {/* center: phone */}
      <div className="hero-phone-wrap" style={{
        position: 'absolute', bottom: 0, left: '50%',
        transform: 'translateX(-50%)', zIndex: 2
      }}>
        <PhoneTour langIndex={langIdx} />
      </div>

      {/* left-column language pills */}
      {leftPills.map((p, i) => {
        const active = p.code === lang.code || p.code === lang.name;
        return (
          <LangPill key={'l' + i} code={p.code} flagIcon={p.flagIcon} active={active}
          onClick={() => onSelect && onSelect(p.code)}
          style={{ position: 'absolute', left: p.dx, top: p.top }} />);

      })}
      {/* right-column language pills */}
      {rightPills.map((p, i) => {
        const active = p.code === lang.code || p.code === lang.name;
        return (
          <LangPill key={'r' + i} code={p.code} flagIcon={p.flagIcon} active={active}
          onClick={() => onSelect && onSelect(p.code)}
          style={{ position: 'absolute', right: p.dx, top: p.top }} />);

      })}

      {/* bottom-right area intentionally left empty for clean composition */}
    </div>);

}

function LangPill({ code, flagIcon, active, style, onClick }) {
  return (
    <button type="button" className={"lang-pill" + (active ? " active" : "")} onClick={onClick} style={{
      background: active ? '#0B1A3F' : '#fff',
      color: active ? '#1FD5F4' : '#0B1A3F',
      border: '1px solid ' + (active ? '#0B1A3F' : '#e8e4d6'),
      borderRadius: 999, padding: '7px 16px',
      fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 12, lineHeight: 1.55,
      opacity: active ? 1 : 0.78,
      boxShadow: active ?
      '0 14px 34px -8px rgba(31,213,244,.6)' :
      '0 6px 16px -8px rgba(11,26,63,.18)',
      transform: `translateY(${active ? -3 : 0}px) scale(${active ? 1.12 : 0.96})`,
      transition: 'all .45s cubic-bezier(.2,.8,.2,1)',
      whiteSpace: 'nowrap',
      zIndex: active ? 5 : 1,
      cursor: 'pointer',
      ...style
    }}>
      <span className="pill-text">{code}</span>
      <span className="pill-flag" aria-hidden="true"><img src={flagIcon} alt="" /></span>
    </button>);

}

// ====== Logo strip ======
function LogoStrip({ label, swapFirst }) {
  let venues = [
  { name: 'Magnolia Plantation & Gardens', file: 'magnolia', boost: 1.1, offsetY: 4 },
  { name: 'Jacksonville Zoo and Botanical Gardens', file: 'jacksonville-zoo' },
  { name: 'Zoo New England', file: 'zoo-new-england' },
  { name: 'Butterfly Pavilion', file: 'butterfly-pavilion' },
  { name: 'North Carolina Aquariums', file: 'nc-aquariums' },
  { name: 'Maui Ocean Center', file: 'maui-ocean' },
  { name: 'SEA LIFE', file: 'sealife' },
  { name: 'Brevard Zoo', file: 'brevard-zoo' },
  { name: 'Columbus Zoo and Aquarium', file: 'columbus-zoo' },
  { name: 'Arnolds Park Amusement Park', file: 'arnolds-park', boost: 1.7 },
  { name: 'Naples Botanical Garden', file: 'naples-botanical', boost: 1.45 },
  { name: 'Army Aviation Museum Foundation', file: 'army-aviation' },
  { name: 'City of Raleigh', file: 'raleigh' },
  { name: 'U.S. Army Airborne & Special Operations Museum', file: 'airborne-museum' },
  { name: 'The Medal of Honor Museum', file: 'medal-of-honor' },
  { name: 'JC Raulston Arboretum', file: 'jc-raulston' }];
  if (swapFirst) {
    venues = venues.slice();
    const i = venues.findIndex((v) => v.file === 'sealife');
    const t = venues[0];
    venues[0] = venues[i];
    venues[i] = t;
  }

  return (
    <div className="logos">
      <div className="wrap">
        <div className="label">{label || 'Trusted by Leading Venues Worldwide'}</div>
        <div className="logo-grid">
          {venues.map((v) =>
          <div key={v.file} className="logo-chip" title={v.name}>
              <img src={venueLogo(v.file)} alt={v.name}
            style={v.boost || v.offsetY ? { transform: `${v.offsetY ? `translateY(${v.offsetY}px) ` : ''}${v.boost ? `scale(${v.boost})` : ''}`.trim() } : undefined} />
            </div>
          )}
        </div>
        <div className="logo-marquee" aria-hidden="true">
          <div className="logo-track">
            {venues.concat(venues).map((v, i) =>
            <div key={v.file + '-' + i} className="logo-chip" title={v.name}>
                <img src={venueLogo(v.file)} alt=""
              style={v.boost || v.offsetY ? { transform: `${v.offsetY ? `translateY(${v.offsetY}px) ` : ''}${v.boost ? `scale(${v.boost})` : ''}`.trim() } : undefined} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>);

}

// ====== Tagline / "Beyond static signage" ======
function BeyondStatic() {
  return (
    <section className="tagline" data-screen-label="Beyond Static">
      <div className="wrap">
        <div style={{ maxWidth: 1180, margin: '0 auto', textAlign: 'center' }}>
          <div className="section-eyebrow" style={{ color: 'var(--cyan)' }}>OUR THESIS</div>
          <h2 style={{
            margin: '0 auto',
            fontSize: 'clamp(36px, 4.4vw, 60px)',
            lineHeight: 1.08,
            letterSpacing: '-0.02em'
          }}>
            Move beyond static, outdated signage.<br />
            <span className="cyan">Meet every guest where they are.</span>
          </h2>
          <p style={{ maxWidth: 760, margin: '24px auto 0', fontSize: 19, lineHeight: 1.55, color: '#c8cee0' }}>
            CheQRboard sits at the intersection of what venues need most: living content, universal access, and measurable revenue.
          </p>
        </div>
      </div>
    </section>);

}

function SignageBeforeAfter() {
  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {/* Before: faded static sign */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
        <div style={{
          flex: 1, padding: '22px 22px', borderRadius: 14,
          background: 'rgba(255,255,255,.05)', border: '1px dashed rgba(255,255,255,.18)',
          position: 'relative', filter: 'grayscale(.4)'
        }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#8e98b8', letterSpacing: '.12em', fontWeight: 700, marginBottom: 6 }}>BEFORE · STATIC SIGN</div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: '#c0c6dd', letterSpacing: '-0.015em' }}>SEA TURTLE (Chelonia mydas)</div>
          <div style={{ marginTop: 6, height: 6, background: 'rgba(255,255,255,.12)', borderRadius: 3, width: '90%' }} />
          <div style={{ marginTop: 4, height: 6, background: 'rgba(255,255,255,.12)', borderRadius: 3, width: '60%' }} />
          <div style={{ marginTop: 12, fontSize: 11, color: '#8e98b8', fontStyle: 'italic' }}>English only · No engagement data</div>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', color: 'var(--cyan)', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.18em', fontWeight: 700 }}>
        ↓ ADD A QR CODE ↓
      </div>
      {/* After: interactive moment */}
      <div style={{
        padding: 22, borderRadius: 18,
        background: 'linear-gradient(135deg, rgba(31,213,244,.14), rgba(31,213,244,.04))',
        border: '1px solid rgba(31,213,244,.4)',
        boxShadow: '0 20px 50px -20px rgba(31,213,244,.2)'
      }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--cyan)', letterSpacing: '.12em', fontWeight: 700, marginBottom: 8 }}>AFTER · CHEQRBOARD MOMENT</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 16, alignItems: 'center' }}>
          <div style={{ background: '#fff', padding: 8, borderRadius: 10 }}>
            <QRCode size={84} />
          </div>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18, color: '#fff', letterSpacing: '-0.015em' }}>
              "Watch Pongo swim →"
            </div>
            <div style={{ fontSize: 13, color: '#c8cee0', marginTop: 4 }}>
              Audio in 12 languages · 90-sec story · Member-conversion prompt
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {['EN', 'ES', '日本語', 'FR', 'DE', 'PT', '+7'].map((c) =>
              <span key={c} style={{
                fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
                background: 'rgba(255,255,255,.08)', color: '#d6dbeb',
                padding: '3px 7px', borderRadius: 6
              }}>{c}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>);

}

// ====== Three Pillars ======
function Pillars() {
  const [hover, setHover] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const pillars = [
  {
    id: 'dynamic',
    tag: '',
    title: 'Dynamic Content Delivery',
    body: 'Deliver the right message at the right moment by updating your content instantly or scheduling changes to go live exactly when you want.',
    stat: '7× engagement',
    hue: 'var(--cyan)'
  },
  {
    id: 'inclusive',
    tag: '',
    title: 'Accessibility & Inclusivity',
    body: 'Support guests in 133+ languages with audio, video, visual, written, and interactive experiences, so more visitors can engage in the way that works best for them.',
    stat: '133+ languages',
    hue: 'var(--cyan)'
  },
  {
    id: 'growth',
    tag: '',
    title: 'Growth & Revenue Streams',
    body: 'Turn guest engagement into donations, memberships, repeat visits, feedback, and on-site revenue, with every interaction measurable in one dashboard.',
    stat: '$11K / month',
    hue: 'var(--cyan)'
  }];

  return (
    <section className="pillars-section" data-screen-label="Three Pillars" id="features">
      <div className="wrap">

        {/* triangular composition */}
        <div className="pillars-tri" style={{ position: 'relative', maxWidth: 980, margin: '0 auto', minHeight: 480 }}>
          {/* connector triangle */}
          <svg className="pillars-svg" viewBox="0 0 980 480" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
            <defs>
              <linearGradient id="pl-gr" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#1FD5F4" stopOpacity=".4" />
                <stop offset="1" stopColor="#0B1A3F" stopOpacity=".2" />
              </linearGradient>
            </defs>
            <polygon points="200,90 780,90 490,400" fill="none" stroke="rgba(31,213,244,.45)" strokeWidth="1.5" strokeDasharray="6 6" />
          </svg>

          {/* center owl — positioned at triangle centroid */}
          <div className="pillars-owl" style={{
            position: 'absolute', left: '50%', top: '40%', transform: 'translate(-50%, -50%)',
            width: 130, height: 130, borderRadius: '50%',
            background: 'radial-gradient(circle at 50% 50%, rgba(31,213,244,.35) 0%, rgba(31,213,244,.12) 40%, transparent 70%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <img src={ASSETS.logoOwl} alt="" style={{ width: 90, height: 'auto' }} />
          </div>

          {/* three pillar cards positioned at triangle vertices, equal width + minHeight */}
          <div className="pillars-slot" style={{
            position: 'absolute', left: '4%', top: '0', width: 'min(300px, 30%)'
          }}>
            <PillarCard p={pillars[0]} hover={hover === 'dynamic'} onHover={(v) => setHover(v ? 'dynamic' : null)} expanded={expanded === 'dynamic'} onToggle={() => setExpanded(expanded === 'dynamic' ? null : 'dynamic')} />
          </div>
          <div className="pillars-slot" style={{
            position: 'absolute', right: '4%', top: '0', width: 'min(300px, 30%)'
          }}>
            <PillarCard p={pillars[1]} hover={hover === 'inclusive'} onHover={(v) => setHover(v ? 'inclusive' : null)} expanded={expanded === 'inclusive'} onToggle={() => setExpanded(expanded === 'inclusive' ? null : 'inclusive')} />
          </div>
          <div className="pillars-slot" style={{
            position: 'absolute', left: '50%', bottom: '0', transform: 'translateX(-50%)', width: 'min(300px, 30%)'
          }}>
            <PillarCard p={pillars[2]} hover={hover === 'growth'} onHover={(v) => setHover(v ? 'growth' : null)} expanded={expanded === 'growth'} onToggle={() => setExpanded(expanded === 'growth' ? null : 'growth')} />
          </div>
        </div>
      </div>
    </section>);

}

function PillarCard({ p, hover, onHover, expanded, onToggle }) {
  return (
    <div
      className={"pillar-card" + (expanded ? " expanded" : "")}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      onClick={() => onToggle && onToggle()}
      style={{
        background: '#fff',
        color: '#0B1A3F',
        border: '2px solid ' + (hover ? 'var(--cyan)' : 'rgba(255,255,255,.08)'),
        borderRadius: 22, padding: 22,
        minHeight: 200,
        display: 'flex', flexDirection: 'column',
        boxShadow: hover ?
        '0 0 0 4px rgba(31,213,244,.18), 0 20px 50px -16px rgba(0,0,0,.45)' :
        '0 10px 30px -12px rgba(11,26,63,.35)',
        transition: 'background .2s ease, color .2s ease, border-color .2s ease, box-shadow .25s ease, transform .25s ease',
        transform: hover ? 'translateY(-6px)' : 'none',
        cursor: 'pointer', position: 'relative', zIndex: 2
      }}>
      <div className="pillar-head">
        <h3 style={{
          fontFamily: 'var(--font-display)', fontWeight: 700,
          fontSize: 22, letterSpacing: '-0.02em', lineHeight: 1.1, margin: 0
        }}>{p.title}</h3>
        <svg className="pillar-chev" width="18" height="18" viewBox="0 0 16 16" fill="none"><path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </div>
      <div className="pillar-body">
        <p style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--ink-2)', margin: 0 }}>
          {p.body}
        </p>
      </div>
    </div>);

}

// ====== Feature pillars / What's in the platform ======
function Capabilities() {
  const features = [
  {
    key: 'audio',
    title: 'Audio Tours',
    body: 'Hardware-free audio tours visitors can launch instantly on their own phones.',
    icon:
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 12a9 9 0 0 1 18 0v6a3 3 0 0 1-3 3h-3v-9h6" />
          <path d="M3 12v6a3 3 0 0 0 3 3h3v-9H3" />
        </svg>

  },
  {
    key: 'multilingual',
    title: 'Self-Guided Multilingual Tours',
    body: 'One QR code, 133+ languages, and self-guided experiences guests can explore at their own pace.',
    icon:
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3a13 13 0 0 1 0 18M12 3a13 13 0 0 0 0 18" />
        </svg>

  },
  {
    key: 'qr',
    title: 'Branded QR Codes',
    body: 'Beautifully branded QR codes that match your venue and drive the right experience.',
    icon:
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
          <path d="M14 14h3v3h-3zM17 17h4M14 20h3M20 14v3" />
        </svg>

  },
  {
    key: 'surveys',
    title: 'Surveys',
    body: 'Capture guest feedback in any language, with responses translated and organized in one place.',
    icon:
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <path d="M14 2v6h6M8 13h8M8 17h5" />
        </svg>

  },
  {
    key: 'donations',
    title: 'Donations',
    body: 'Turn moments of connection into frictionless giving with donation prompts built into the guest journey.',
    icon:
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>

  },
  {
    key: 'games',
    title: 'Game Design',
    body: 'Create custom scavenger hunts, trivia, seasonal challenges, and interactive experiences for any audience.',
    icon:
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="6" width="20" height="12" rx="3" />
          <path d="M7 10v4M5 12h4M15 12h.01M18 14h.01M15 14h.01M18 12h.01" />
        </svg>

  },
  {
    key: 'ai',
    title: 'Optional AI Content Creation',
    body: 'Draft, translate, and adapt tours, trivia, surveys, and educational content faster, with tools designed for busy venue teams.',
    icon:
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3l1.8 4.2L18 9l-4.2 1.8L12 15l-1.8-4.2L6 9l4.2-1.8z" />
          <path d="M18 14l.9 2.1L21 17l-2.1.9L18 20l-.9-2.1L15 17l2.1-.9z" />
        </svg>

  },
  {
    key: 'ticketing',
    title: 'Event Ticketing',
    soon: true,
    body: 'Sell tickets for events, programs, and seasonal experiences, with engagement and attendance insights all in one place.',
    icon:
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4z" />
          <path d="M14 6v12" strokeDasharray="2 2" />
        </svg>

  }];


  return (
    <section className="features features-compact" data-screen-label="Capabilities" id="solutions">
      <div className="wrap">
        <div className="head">
          <h2 className="section-title">No more juggling separate<br/>tools for everything.</h2>
          <p className="section-lede">
            One platform for every visitor moment.
          </p>
        </div>
        <div className="feature-row">
          {features.map((f) =>
          <div className="feature-mini" key={f.key}>
              <div className="feature-mini-icon">{f.icon}</div>
              <div className="feature-mini-body">
                <h3>{f.title}{f.soon && <span className="soon-tag">Coming soon</span>}</h3>
                <p>{f.body}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}

function FeatureCard({ tag, title, body, badge, wide, children }) {
  return (
    <div className="feature-card" style={wide ? { gridColumn: 'span 2' } : {}}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div className="tag">{tag}</div>
        <div style={{
          fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700,
          background: 'var(--cyan-tint)', color: 'var(--cyan-2)',
          padding: '4px 10px', borderRadius: 999
        }}>{badge}</div>
      </div>
      <h3>{title}</h3>
      <p>{body}</p>
    </div>);

}

function MultilingualViz() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % LANGS.length), 1400);
    return () => clearInterval(id);
  }, []);
  return (
    <div style={{
      padding: 22, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 14
    }}>
      <div style={{
        background: '#fff', borderRadius: 14, padding: '14px 16px',
        border: '1px solid #e8e4d6', display: 'flex', alignItems: 'center', gap: 12
      }}>
        <div style={{
          fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
          background: '#0B1A3F', color: '#1FD5F4', padding: '3px 8px', borderRadius: 6
        }}>EN</div>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: '#0B1A3F', letterSpacing: '-0.01em' }}>
          Welcome to the Aviary
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', color: '#00b4d8', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.16em', fontWeight: 700 }}>
        ↓ AUTO-TRANSLATE ↓
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {LANGS.slice(0, 4).map((l, i) => {
          const on = i === active % 4;
          return (
            <div key={l.code} style={{
              background: on ? '#0B1A3F' : '#fff',
              color: on ? '#fff' : '#0B1A3F',
              borderRadius: 10, padding: '8px 12px',
              border: '1px solid ' + (on ? '#0B1A3F' : '#e8e4d6'),
              display: 'flex', alignItems: 'center', gap: 10,
              transition: 'all .25s ease'
            }}>
              <div style={{
                fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 700,
                background: on ? '#1FD5F4' : '#f6f4ec', color: on ? '#0B1A3F' : '#6c7596',
                padding: '2px 6px', borderRadius: 4, minWidth: 28, textAlign: 'center'
              }}>{l.code}</div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, letterSpacing: '-0.005em' }}>
                {l.greeting}
              </div>
            </div>);

        })}
      </div>
    </div>);

}

// ====== Showcase (easy-to-use editor) ======
function Showcase() {
  return (
    <section className="showcase showcase-split showcase-laptop-left" data-screen-label="Editor Showcase">
      <div className="wrap">
        <div className="showcase-top">
          <h2 className="section-title" style={{
            fontSize: 'clamp(36px, 4.4vw, 56px)',
            margin: 0
          }}>
            Create in Minutes, Not Months
          </h2>
        </div>
        <div className="showcase-grid">
          <div className="showcase-laptop-side">
            <EditorMock />
          </div>
          <div className="showcase-text">
            <div className="section-eyebrow">CHEQRBOARD STUDIO</div>
            <p style={{ color: 'var(--ink-2)', fontSize: 17, margin: '14px 0 24px', maxWidth: 520 }}>
              CheQRboard helps teams create, update, schedule, and publish high-quality visitor experiences in minutes, with AI support when you want it and full control when you don’t.
            </p>
            <ul className="checks">
              <li>
                <span className="ck">✓</span>
                <span><strong>Drag-and-drop content building</strong></span>
              </li>
              <li>
                <span className="ck">✓</span>
                <span><strong>AI-assisted content creation</strong></span>
              </li>
              <li>
                <span className="ck">✓</span>
                <span><strong>Real-time preview and publishing</strong></span>
              </li>
              <li>
                <span className="ck">✓</span>
                <span><strong>No technical expertise required</strong></span>
              </li>
              <li>
                <span className="ck">✓</span>
                <span><strong>Time-based and seasonal content scheduling</strong></span>
              </li>
            </ul>
            <div className="ctas">
              <a href={BOOK_DEMO_HREF} className="btn btn-primary" data-book-demo>Book a demo<span className="arrow">→</span></a>
            </div>
          </div>
        </div>
      </div>
    </section>);

}

// ====== Pricing ======
function Pricing() {
  const [openTier, setOpenTier] = useState(1);
  const tiers = [
  {
    name: 'Tier 1: Starter',
    price: '$999',
    cadence: '/month',
    year: '$11,389/year — Save 5%',
    features: [
    ['Languages', 'Native + 4'],
    ['Partner Success Strategist', 'Up to 2 hours per month'],
    ['Content creation support', '$80 per hour'],
    ['Basic analytics', null],
    ['Donation & payment processing', '2%']],

    cta: 'Talk to sales'
  },
  {
    name: 'Tier 2: Standard',
    price: '$1,499',
    cadence: '/month',
    year: '$16,549/year — Save 8% · Price Lock 2 Yrs',
    popular: true,
    features: [
    ['Languages', 'Native + 6'],
    ['Partner Success Strategist', 'Up to 5 hours per month'],
    ['Content creation support', 'Up to 5 hours per month'],
    ['Basic analytics', null],
    ['Game design templates', null],
    ['Survey feature enabled', null],
    ['Donation & payment processing', '1%']],

    cta: 'Start with Standard'
  },
  {
    name: 'Tier 3: Enterprise',
    price: '$3,999',
    cadence: '/month',
    year: '$43,189/year — Save 10% · Price Lock 3 Yrs',
    features: [
    ['Languages', 'Native + 10'],
    ['Partner Success Strategist', 'Up to 15 hours per month'],
    ['Content creation support', 'Up to 15 hours per month'],
    ['Enhanced analytics', null],
    ['Game design templates', null],
    ['Annual custom game design support', '60 hours per year'],
    ['Survey feature enabled', null],
    ['Donation & payment processing', 'Fee-free (Stripe fees only)']],

    cta: 'Book a demo'
  }];

  return (
    <section className="pricing" id="pricing" data-screen-label="Pricing">
      <div className="wrap">

        <div className="tier-grid">
          {tiers.map((t, i) =>
          <div className={"tier" + (t.popular ? " tier-popular" : "") + (openTier === i ? " tier-open" : "")} key={i}>
              {t.popular && <div className="popular-pill">Most Popular</div>}
              <div className="tier-icon">
                {i === 0 && (
              /* sprout */
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 21v-8" />
                    <path d="M12 13C12 9.7 9.5 7 6 7c0 3.3 2.5 6 6 6z" />
                    <path d="M12 13c0-3.9 2.9-7 6.5-7 0 3.9-2.9 7-6.5 7z" />
                  </svg>)
              }
                {i === 1 && (
              /* mountain peak */
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 19h18L13.5 6l-3.2 5.4-2-2.2z" />
                    <path d="M10.3 11.4l1.7 1.9 1.5-2.2" />
                  </svg>)
              }
                {i === 2 && (
              /* tower building */
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2.5 21.5h19" />
                    <path d="M8 21.5V3.5h8v18" />
                    <path d="M16 8.5h3.5v13" />
                    <path d="M8 11.5H4.5v10" />
                    <path d="M10.4 6.6h3.2M10.4 10h3.2M10.4 13.4h3.2M10.4 16.8h3.2" />
                  </svg>)
              }
              </div>
              <div className="tier-name">{t.name}</div>
              <div className="tier-price">
                <span className="amount">{t.price}</span>
                <span className="cadence">{t.cadence}</span>
              </div>
              <div className="tier-year">{t.year}</div>

              <button
                type="button"
                className="tier-toggle"
                aria-label="Toggle plan details"
                aria-expanded={openTier === i}
                onClick={() => setOpenTier(openTier === i ? -1 : i)}>
                <span className="tier-chev">⌄</span>
              </button>

              <ul className="tier-features">
                {t.features.map(([label, value], j) =>
              <li key={j}>
                    <span className="check">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    <div>
                      <strong>{label}</strong>
                      {value && <span className="val">: {value}</span>}
                    </div>
                  </li>
              )}
              </ul>

              <a href={BOOK_DEMO_HREF} className={"btn " + (t.popular ? "btn-cyan" : "btn-outline")} data-book-demo style={{ width: '100%', justifyContent: 'center', marginTop: 'auto' }}>
                {t.cta}<span className="arrow">→</span>
              </a>
            </div>
          )}
        </div>

        <div className="pricing-head pricing-head-solo">
          <div className="setup-card">
            <div className="setup-card-eyebrow">One-Time Setup Fee</div>
            <div className="setup-card-price-line">
              <span className="setup-card-price-tail"><span>$500 - </span>$7,000<span className="setup-card-asterisk">*</span></span>
            </div>
            <div className="setup-card-desc">
              *Based on venue size, annual attendance, and implementation scope.
              Includes onboarding, configuration, QR design, and launch support.
            </div>
          </div>
        </div>

        <div className="pricing-foot" style={{ display: 'none' }}>
          Not sure which plan? <a href={BOOK_DEMO_HREF} data-book-demo>Talk to our team</a> — we&apos;ll custom build a plan based on your venue.
        </div>
      </div>
    </section>);

}
function Outcomes() {
  return (
    <section className="showcase showcase-split" data-screen-label="Analytics">
      <div className="wrap">
        <div className="showcase-top">
          <h2 className="section-title" style={{ fontSize: 'clamp(36px, 4.4vw, 56px)', margin: 0 }}>
            Enhanced Analytics
          </h2>
        </div>
        <div className="showcase-grid">
          <div className="showcase-text">
            <div className="section-eyebrow">BEYOND SCANS AND CLICKS</div>
            <p style={{ color: 'var(--ink-2)', fontSize: 17, margin: '14px 0 24px', maxWidth: 620 }}>
              We track engagement beyond just scans or clicks. It's about what happens <strong>after</strong> they scan. CheQRboard helps you learn what guests like and don't like, so you can deliver the right content to the right audience.
            </p>
            <ul className="checks">
              <li>
                <span className="ck">✓</span>
                <span><strong>Scans & engagement:</strong> Track how guests engage with your content and spot what's working in real time.</span>
              </li>
              <li>
                <span className="ck">✓</span>
                <span><strong>Donations:</strong> See what guests engage with most, so every donation prompt lands where it matters.</span>
              </li>
              <li>
                <span className="ck">✓</span>
                <span><strong>Survey insights:</strong> Survey guests in their own language and get feedback you can actually act on.</span>
              </li>
              <li>
                <span className="ck">✓</span>
                <span><strong>Language breakdown:</strong> See what languages guests select, so you know who you're reaching.</span>
              </li>
            </ul>
            <div className="ctas">
              <a href={BOOK_DEMO_HREF} className="btn btn-primary" data-book-demo>Book a demo<span className="arrow">→</span></a>
            </div>
          </div>
          <div className="showcase-laptop-side">
            <AnalyticsMock />
          </div>
        </div>
      </div>
    </section>);

}

// ====== Industries / Use cases ======
function Industries() {
  const cases = [
  { name: 'Amusement Parks', iconSrc: industryIcon('amusement-parks'), desc: 'Queue games, ride storytelling, scavenger hunts, and sponsor-friendly engagement.' },
  { name: 'Aquariums', iconSrc: industryIcon('aquariums'), desc: 'Audio tours, multilingual interpretation, conservation prompts, and member upsells.' },
  { name: 'Botanical Gardens', iconSrc: industryIcon('botanical-gardens'), desc: 'Plant stories, seasonal trails, accessibility tools, and donor moments.' },
  { name: 'City Parks & Downtowns', iconSrc: industryIcon('city-parks-downtowns'), desc: 'Native plant trails, public art tours, civic surveys, and local history.' },
  { name: 'Museums & Historic Sites', iconSrc: industryIcon('museums-historic-sites'), desc: 'Self-guided tours, exhibit interpretation, donor moments, and multilingual accessibility.' },
  { name: 'Zoos', iconSrc: industryIcon('zoos'), desc: 'Wildlife stories, conservation prompts, scavenger hunts, and donor engagement.' }];

  return (
    <section className="use-cases" data-screen-label="Industries" id="case-studies">
      <div className="wrap">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 32, gap: 24, flexWrap: 'wrap' }}>
          <div>
            <div className="section-eyebrow">Built for any venue</div>
            <h2 className="section-title" style={{ fontSize: 'clamp(28px, 3vw, 40px)' }}>
              Built for venues, attractions, and destinations.
            </h2>
          </div>
          <a href="#" className="btn btn-outline uc-header-btn">Other Industries<span className="arrow">→</span></a>
        </div>
        <div className="row">
          {cases.map((c) =>
          <div className="uc" key={c.name}>
              <div className="uc-icon"><NavMenuIcon iconSrc={c.iconSrc} icon={c.icon} /></div>
              <h4>{c.name}</h4>
              <div style={{ fontSize: 12, marginTop: 6, opacity: .75, lineHeight: 1.35 }}>{c.desc}</div>
            </div>
          )}
        </div>
        <div style={{ marginTop: 28 }} />
      </div>
    </section>);

}

// ====== Testimonials ======
function Testimonials() {
  return (
    <section className="testimonials" data-screen-label="Testimonials">
      <div className="wrap">
        <div className="grid">
          <div className="t-card featured">
            <div>
              <p className="quote">
                CheQRboard is a <span className="hl">flexible and effective solution</span> that helped us offer a more inclusive exhibition. Their easy-to-use platform made implementation simple for our team, while visitors could instantly access content in their preferred language by scanning a QR code.
              </p>
            </div>
            <div className="person">
              <div className="avatar" style={{ backgroundImage: `url(${ASSETS.javanSutton})`, backgroundSize: 'cover', backgroundPosition: 'center', color: 'transparent' }}>JS</div>
              <div>
                <div className="who">Javan Sutton</div>
                <div className="role">Director of Interpretation &amp; Planning, North Carolina Museum of Natural Sciences</div>
              </div>
            </div>
          </div>

          <div className="t-card">
            <div>
              <p className="quote">
                With just a two-person team, we launched our first <span className="hl">Trivia Game in a couple of hours</span> and have already enhanced our visitor experience significantly.
              </p>
            </div>
            <div className="person">
              <div className="avatar" style={{ backgroundImage: `url(${ASSETS.leahDunkle})`, backgroundSize: 'cover', backgroundPosition: 'center', color: 'transparent' }}>LD</div>
              <div>
                <div className="who">Leah Dunkle</div>
                <div className="role">Executive Coordinator · Army Aviation Museum</div>
              </div>
            </div>
          </div>

          <div className="t-card">
            <div>
              <p className="quote">
                Connecting communities with wildlife and wild places begins with <span className="hl">meaningful experiences</span>. CheQRboard helps us create engaging moments that inspire curiosity, deepen connections, and encourage guests to become advocates for wildlife and conservation.
              </p>
            </div>
            <div className="person">
              <div className="avatar" style={{ backgroundImage: `url(${ASSETS.leanneWhite})`, backgroundSize: 'cover', backgroundPosition: 'center', color: 'transparent' }}>LW</div>
              <div>
                <div className="who">Leanne White</div>
                <div className="role">Chief Impact Officer, Jacksonville Zoo and Botanical Gardens</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}

// ====== CSM strip ======
function CSMStrip() {
  const [openHelp, setOpenHelp] = useState(null);
  return (
    <section className="csm" data-screen-label="CSM" id="team">
      <div className="wrap">
        <div className="csm-card">
          <div>
            <div className="section-eyebrow">WHITE-GLOVE SUPPORT</div>
            <h3>Your team, backed by ours.</h3>
            <p>
              You decide how involved you want us to be. Your CheQRboard team can help scope new projects, write content, design interactive experiences, build your first tours, train your staff, and support future launches whenever you need us.
            </p>
            <div className="ctas csm-cta-desktop">
              <Link to="/case-studies/" className="btn btn-primary">See Our Success Stories<span className="arrow">→</span></Link>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{
              background: 'linear-gradient(135deg, #0B1A3F 0%, #14305f 100%)',
              borderRadius: 22,
              boxShadow: '0 22px 48px -18px rgba(11,26,63,.5)',
              overflow: 'hidden'
            }}>
              {/* Header row */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 16,
                padding: '24px 28px',
                borderBottom: '1px solid rgba(255,255,255,.1)'
              }}>
                <div style={{
                  width: 54, height: 54, borderRadius: 16,
                  background: 'rgba(31,213,244,.16)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flex: 'none'
                }}>
                  <svg width="34" height="34" viewBox="0 0 512 512" fill="none" stroke="#1FD5F4" strokeWidth="26" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M120 150 H210 L250 130 Q270 120 295 130 L380 165"/>
                    <path d="M210 150 Q160 195 155 235 Q152 265 185 270 Q215 273 240 245 L255 230"/>
                    <path d="M255 230 L360 320 Q380 338 362 358 Q344 376 324 358 L268 308"/>
                    <path d="M324 358 Q342 378 322 396 Q304 412 286 394 L240 350"/>
                    <path d="M286 394 Q302 414 282 430 Q266 444 248 428 L215 396"/>
                    <path d="M248 428 Q262 446 244 460 Q228 472 210 458 L120 380"/>
                    <path d="M40 120 L100 122 Q112 122 108 138 L78 300 Q75 312 62 310 L40 308 Z"/>
                    <path d="M472 130 L410 134 Q398 135 402 150 L440 305 Q443 316 456 314 L472 312 Z"/>
                  </svg>
                </div>
                <div>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, color: '#fff', fontSize: 18, lineHeight: 1.1 }}>A true extension of your team</div>
                  <div className="csm-sub" style={{ color: '#9fb0d4', fontSize: 13, marginTop: 3 }}>From launch planning to content creation, our team is here whenever you need extra hands, ideas, or strategy.</div>
                </div>
              </div>
              {/* Launch plan */}
              <div className="csm-help" style={{ padding: '20px 28px 24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#1FD5F4', letterSpacing: '.12em', fontWeight: 700, whiteSpace: 'nowrap' }}>HOW WE CAN HELP</div>
                </div>
                {[
                { t: 'Project strategy', desc: 'Scope your tours, campaigns, donation flows, and sponsorships around the goals that matter most.' },
                { t: 'Content writing', desc: 'Turn your signage, stories, and ideas into experiences visitors want to explore.' },
                { t: 'Game design', desc: 'Build scavenger hunts, trivia, and challenges that keep guests engaged longer.' },
                { t: 'Training & ongoing support', desc: 'Onboard your team, answer questions, and help you improve with every launch.' }].
                map((s, i) =>
                <div className={"csm-help-item" + (openHelp === i ? " open" : "")} key={i} onClick={() => setOpenHelp(openHelp === i ? null : i)} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 0', borderTop: i === 0 ? 'none' : '1px dashed rgba(255,255,255,.12)', cursor: 'pointer' }}>
                    <div style={{
                    width: 22, height: 22, borderRadius: 999,
                    background: '#1FD5F4',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#0B1A3F', fontWeight: 700, fontSize: 12, flex: 'none', marginTop: 1
                  }}>✓</div>
                    <div style={{ flex: 1 }}>
                      <div className="csm-help-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                        <span style={{ fontWeight: 700, color: '#fff', fontSize: 14 }}>{s.t}</span>
                        <svg className="csm-help-chev" width="15" height="15" viewBox="0 0 16 16" fill="none" style={{ flex: 'none', transition: 'transform .25s ease', transform: openHelp === i ? 'rotate(180deg)' : 'none' }}><path d="M4 6l4 4 4-4" stroke="#9fb0d4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </div>
                      <div className="csm-help-desc" style={{ color: '#9fb0d4', fontSize: 12.5, lineHeight: 1.45, maxHeight: openHelp === i ? 160 : 0, overflow: 'hidden', transition: 'max-height .3s ease, margin-top .3s ease', marginTop: openHelp === i ? 6 : 0 }}>{s.desc}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="ctas csm-cta-mobile">
            <Link to="/case-studies/" className="btn btn-primary">See Our Success Stories<span className="arrow">→</span></Link>
          </div>
        </div>
      </div>
    </section>);

}

function TeamCard({ person }) {
  const roleLen = person.role.length;
  const roleClass = roleLen > 22 ? 'long xl' : roleLen > 18 ? 'long' : '';

  return (
    <div className="team-card">
      <div className="team-photo" tabIndex={person.facts ? 0 : undefined}>
        <img
          src={person.img}
          alt={person.role}
          style={{ objectPosition: person.pos || 'center' }} />
        {person.facts && (
          <div className="team-facts">
            <div className="team-facts-title">Fun fact</div>
            <ul>
              {person.facts.map((fact, i) => {
                const len = String(fact).length;
                const cls = len > 180 ? 'long xl' : len > 130 ? 'long' : '';
                return <li key={i} className={cls}>{fact}</li>;
              })}
            </ul>
            {person.linkedin && (
              <a className="team-li" href={person.linkedin} target="_blank" rel="noopener noreferrer" aria-label={person.name + ' on LinkedIn'}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9h4v12H3V9Zm7 0h3.8v1.7h.05c.53-1 1.83-2.05 3.77-2.05 4.03 0 4.78 2.65 4.78 6.1V21H18v-5.6c0-1.34-.02-3.06-1.87-3.06-1.87 0-2.15 1.46-2.15 2.96V21H10V9Z" /></svg>
              </a>
            )}
          </div>
        )}
      </div>
      <h3>{person.name}</h3>
      <div className={'role' + (roleClass ? ' ' + roleClass : '')}>{person.role}</div>
    </div>);

}

function TeamSection() {
  const scrollToPets = () => {
    const target = document.getElementById('real-team');
    if (target) {
      window.scrollTo({
        top: target.getBoundingClientRect().top + window.pageYOffset - 30,
        behavior: 'smooth',
      });
    }
  };

  return (
    <Fragment>
      <div className="team-hero">
        <div className="wrap">
          <h1 className="team-hero-title">Meet the humans behind the mission</h1>
          <div
            className="team-hero-sub"
            role="button"
            tabIndex={0}
            onClick={scrollToPets}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                scrollToPets();
              }
            }}>
            and the pets behind the motivation
            <span className="team-hero-paw" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="currentColor"><ellipse cx="12" cy="15.5" rx="5" ry="4.2" /><ellipse cx="5.6" cy="9.6" rx="2.3" ry="3" /><ellipse cx="18.4" cy="9.6" rx="2.3" ry="3" /><ellipse cx="9.4" cy="5.2" rx="2.2" ry="2.9" /><ellipse cx="14.6" cy="5.2" rx="2.2" ry="2.9" /></svg>
            </span>
          </div>
        </div>
      </div>

      <section className="team-section">
        <div className="wrap">
          <div className="team-section-spacer" aria-hidden="true" />
          <div className="team-lead">
            {LEADERS.map((person) => <TeamCard person={person} key={person.slot} />)}
          </div>
        </div>
      </section>

      <section id="real-team" style={{ background: 'var(--paper)', padding: '48px 0 40px' }}>
        <div className="wrap">
          <div style={{ textAlign: 'center', maxWidth: 680, margin: '0 auto 44px' }}>
            <h2 className="section-title">The real bosses.</h2>
            <p style={{ fontSize: 16, color: 'var(--ink-2)', margin: '10px auto 0', maxWidth: 560, lineHeight: 1.55 }}>
              Unpaid, unbothered, and absolutely essential.
            </p>
          </div>
          <div className="team-lead">
            {PETS.map((person) => <TeamCard person={person} key={person.slot} />)}
          </div>
        </div>
      </section>
    </Fragment>);

}

// ====== Final CTA ======
function FinalCTA(props) {
  const { noQr, sub, heading, hideEyebrow, eyebrow } = props || {};
  return (
    <section className={"cta" + (noQr ? " cta-noqr" : "")} data-screen-label="CTA" id="contact">
      <div className="wrap">
        <div className="cta-card">
          <div>
            {!hideEyebrow &&
            <div className="section-eyebrow" style={{ color: 'var(--cyan)' }}>{eyebrow || 'READY TO GET STARTED?'}</div>}
            <h2>
              {heading || <Fragment>Reach every guest with the <span className="cyan">right story</span>, in their language, at just the right moment.</Fragment>}
            </h2>
            {sub !== null &&
            <p>
              {sub || 'See how venues like yours are using CheQRboard today.'}
            </p>}
            <div className="ctas">
              <a href={BOOK_DEMO_HREF} className="btn btn-cyan" data-book-demo>Book a demo<span className="arrow">→</span></a>
            </div>
          </div>
          {!noQr &&
          <div className="cta-qr" style={{ display: 'flex', justifyContent: 'center' }}>
            <a href={BOOK_DEMO_HREF} data-book-demo className="cta-qr-link" aria-label="Book a demo" style={{ background: '#fff', padding: 18, borderRadius: 22, boxShadow: '0 30px 70px -20px rgba(0,0,0,.4)', textDecoration: 'none', color: 'inherit' }}>
              <img src={ASSETS.bookQr} alt="Scan to book a demo" style={{ width: 160, height: 160, display: 'block' }} />
              <div style={{ marginTop: 10, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 11, color: '#0B1A3F', fontWeight: 700, letterSpacing: '.08em' }}>
                SCAN TO BOOK
              </div>
            </a>
          </div>}
        </div>
      </div>
    </section>);

}

// ====== Contact page form ======
function ContactSection() {
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          'first-name': formData.get('first-name'),
          'last-name': formData.get('last-name'),
          email: formData.get('email'),
          phone: '',
          message: [
            formData.get('job-title') && `Job title: ${formData.get('job-title')}`,
            formData.get('company-name') && `Company: ${formData.get('company-name')}`,
            formData.get('country') && `Country: ${formData.get('country')}`,
            formData.get('hear-about') && `How they heard about us: ${formData.get('hear-about')}`,
            formData.get('message') && String(formData.get('message')),
          ].filter(Boolean).join('\n'),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Something went wrong. Please try again.');
        return;
      }

      form.reset();
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <div className="wrap contact-grid">
        <div className="contact-form-col">
          {sent ? (
            <div className="contact-success">
              <div className="contact-success-icon">✓</div>
              <h1>Message sent!</h1>
              <p className="contact-lede">
                Thanks for reaching out. A member of our team will be in touch within one business day.
              </p>
            </div>
          ) : (
            <Fragment>
              <div className="section-eyebrow" style={{ color: 'var(--cyan)' }}>CONTACT US</div>
              <h1>Let's talk.</h1>
              <p className="contact-lede">
                Tell us a bit about your venue and what you'd like guests to experience. We'll get back to you within one business day.
              </p>
              <form className="contact-form" onSubmit={handleSubmit}>
                <label>
                  Business email address *
                  <input type="email" name="email" required />
                </label>
                <div className="cf-row">
                  <label>
                    First name *
                    <input type="text" name="first-name" required />
                  </label>
                  <label>
                    Last name *
                    <input type="text" name="last-name" required />
                  </label>
                </div>
                <div className="cf-row">
                  <label>
                    Job title
                    <input type="text" name="job-title" />
                  </label>
                  <label>
                    Company name
                    <input type="text" name="company-name" />
                  </label>
                </div>
                <div className="cf-row">
                  <label>
                    Country
                    <select name="country" defaultValue="">
                      <option value="" disabled>Select…</option>
                      <option>United States</option>
                      <option>Canada</option>
                      <option>United Kingdom</option>
                      <option>Australia</option>
                      <option>Other</option>
                    </select>
                  </label>
                  <label>
                    How did you hear about us?
                    <select name="hear-about" defaultValue="">
                      <option value="" disabled>Select…</option>
                      <option>Referral from another venue</option>
                      <option>Conference or event</option>
                      <option>Social media</option>
                      <option>Other</option>
                    </select>
                  </label>
                </div>
                <label>
                  Additional details
                  <textarea name="message" rows={4} />
                </label>
                <label className="cf-consent">
                  <input type="checkbox" name="consent" required />
                  <span>By submitting this form, I consent to CheQRboard processing my personal data in accordance with the <Link to="/privacy/">Privacy Policy</Link> and to sending me additional information, which I may unsubscribe from at any time.</span>
                </label>
                <div className="contact-note">
                  <h3>No Solicitation</h3>
                  <p>We're always happy to connect with organizations and individuals interested in learning more about what we do. Please note that this form is intended for partnership, collaboration, and general inquiries, not unsolicited sales or promotional solicitations.</p>
                </div>
                {error && <p className="contact-error">{error}</p>}
                <button type="submit" className="btn btn-cyan" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Send message'}<span className="arrow">→</span>
                </button>
              </form>
            </Fragment>
          )}
        </div>
        <div className="contact-owl-col">
          <img src={ASSETS.logoOwl} alt="CheQRboard owl" />
        </div>
      </div>
    </div>);

}

// ====== Case Studies page ======
function CaseStudiesSection() {
  const featured = FEATURED_CASE_STUDY;

  return (
    <Fragment>
      <div className="cs-hero">
        <div className="wrap">
          <div className="cs-feature-headwrap">
            <div className="cs-feature-heading">Featured Success Story</div>
          </div>
          <Link to={`/case-studies/${featured.id}/`} className="cs-feature">
            <div className="cs-feature-media">
              <img src={featured.image} alt={featured.title} />
            </div>
            <div className="cs-feature-copy">
              <div className="cs-feature-venue">{featured.venue}</div>
              <h1>{featured.title}</h1>
              <p>{featured.body}</p>
              <div className="cs-feature-metrics" style={{ display: 'none' }}>
                {featured.metrics.map((m) =>
                  <div className="cs-feature-metric" key={m.lbl}>
                    <div className="num">{m.num}</div>
                    <div className="lbl">{m.lbl}</div>
                  </div>
                )}
              </div>
              <span className="cs-feature-link">Read the story<span className="arrow">→</span></span>
            </div>
          </Link>
        </div>
      </div>

      <section className="cs-more">
        <div className="wrap">
          <div className="cs-more-head">
            <h2 className="section-title cs-more-title">More Stories of Engagement, Education, and Impact</h2>
          </div>
          <div className="cs-grid">
            {MORE_CASE_STUDIES.map((s) =>
              <Link to={`/case-studies/${s.id}/`} className="cs-card" key={s.id}>
                <div className="cs-top">
                  <span className="cs-tag">{s.tag}</span>
                  <img
                    src={s.image}
                    alt={s.title}
                    style={{
                      objectFit: s.fit || 'cover',
                      objectPosition: s.pos || 'center',
                      background: s.fit === 'contain' ? '#0b1220' : 'transparent',
                    }}
                  />
                </div>
                <div className="cs-body">
                  <h3>{s.title}</h3>
                  <p>{s.body}</p>
                  <div className="cs-metrics">
                    {s.metrics.map((m) =>
                      <div className="cs-metric" key={m.lbl}>
                        <div className="num">{m.num}</div>
                        <div className="lbl">{m.lbl}</div>
                      </div>
                    )}
                  </div>
                  <span className="cs-readmore">Read the story<span className="arrow">→</span></span>
                </div>
              </Link>
            )}
          </div>
        </div>
      </section>

      <section className="cs-cta">
        <div className="wrap">
          <div className="cs-cta-card">
            <div className="section-eyebrow cs-cta-eyebrow">STILL SCROLLING?</div>
            <h2>At this point, you might as well book a demo.</h2>
            <p>No pressure. Just 20 minutes, a few ideas for your venue, and absolutely no 47-slide sales deck.</p>
            <div className="ctas">
              <a href={BOOK_DEMO_HREF} className="btn btn-cyan" data-book-demo>Book a demo<span className="arrow">→</span></a>
            </div>
          </div>
        </div>
      </section>
    </Fragment>
  );
}

// ====== Footer ======
function Footer() {
  const cols = [
  { h: 'Platform', items: ['Studio', 'Multilingual', 'Surveys', 'Donations', 'Games', 'Analytics'] },
  { h: 'Solutions', items: ['Zoos', 'Museums', 'Aquariums', 'Parks', 'Botanical', 'Amusement'] },
  { h: 'Company', items: ['Team', 'Careers', 'Press', 'Contact'] },
  { h: 'Resources', items: ['Case studies', 'Pricing', 'Help center', 'Status'] }];

  return (
    <footer>
      <div className="wrap">
        <div className="top">
          <div className="brand-blurb">
            <div style={{ marginBottom: 14 }}>
              <img src={ASSETS.logoWhite} alt="CheQRboard" style={{ height: 38, width: 'auto' }} />
            </div>
            <p>The guest-engagement platform for venues. Right guest, right message, right time, every time.</p>
          </div>
          {cols.map((c) =>
          <div key={c.h}>
              <h5>{c.h}</h5>
              <ul>
                {c.items.map((it) => {
                  const href = FOOTER_LINKS[it];
                  const unavailable = !href;
                  return (
                    <li key={it}>
                      {unavailable ? (
                        <a
                          href="#"
                          className="link-unavailable"
                          aria-disabled="true"
                          onClick={preventUnavailableNav}>
                          {it}
                        </a>
                      ) : (
                        <Link to={href}>{it}</Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
        <div className="bottom">
          <div>© {new Date().getFullYear()} CheQRboard · Made for venues that move.</div>
          <div className="legal">
            <Link to="/privacy/">Privacy</Link>
            <Link to="/terms/">Terms</Link>
          </div>
        </div>
      </div>
    </footer>);

}

// ====== Book a demo modal ======
function DemoModal() {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const venue = formData.get('venue');
    const venueType = formData.get('venue-type');
    const notes = formData.get('message');

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          'first-name': formData.get('first-name'),
          'last-name': formData.get('last-name'),
          email: formData.get('email'),
          phone: '',
          message: [
            venue && `Venue: ${venue}`,
            venueType && `Type: ${venueType}`,
            notes && String(notes),
          ].filter(Boolean).join('\n'),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Something went wrong. Please try again.');
        return;
      }

      form.reset();
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const openFromHash = () => {
      if (shouldOpenDemoModalFromHash(window.location.hash)) {
        setSent(false);
        setOpen(true);
      }
    };

    const onClick = (e) => {
      if (!isBookDemoTrigger(e.target)) return;
      e.preventDefault();
      setSent(false);
      setOpen(true);
    };

    openFromHash();
    document.addEventListener('click', onClick);
    window.addEventListener('hashchange', openFromHash);
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick);
      window.removeEventListener('hashchange', openFromHash);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  return (
    <div className="demo-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
      <div className="demo-modal" role="dialog" aria-modal="true" aria-label="Book a demo">
        <button className="demo-close" aria-label="Close" onClick={() => setOpen(false)}>×</button>

        {sent ? (
          <div className="demo-success">
            <div className="demo-success-icon">✓</div>
            <h2>You're all set!</h2>
            <p>Thanks for reaching out. A member of our team will be in touch within one business day to schedule your walkthrough.</p>
            <button className="btn btn-cyan" onClick={() => setOpen(false)} style={{ justifyContent: 'center' }}>Close</button>
          </div>
        ) : (
          <Fragment>
            <div className="demo-head">
              <h2>Book a demo</h2>
              <p>See how CheQRboard turns your signage into multilingual guest experiences. We'll tailor the walkthrough to your venue.</p>
            </div>
            <form className="demo-form" onSubmit={handleSubmit}>
              <div className="demo-row">
                <input type="text" name="first-name" placeholder="First name" required />
                <input type="text" name="last-name" placeholder="Last name" required />
              </div>
              <input type="email" name="email" placeholder="Work email address*" required />
              <input type="text" name="venue" placeholder="Venue / organization name" required />
              <select name="venue-type" required defaultValue="">
                <option value="" disabled>Type of venue</option>
                <option>Zoo</option>
                <option>Aquarium</option>
                <option>Botanical Garden</option>
                <option>Museum / Historic Site</option>
                <option>City Park / Downtown</option>
                <option>Amusement Park</option>
                <option>Other</option>
              </select>
              <textarea name="message" rows={3} placeholder="Anything else you'd like us to know?"></textarea>
              {error && <p style={{ color: '#ff8a80', fontSize: 14, margin: 0 }}>{error}</p>}
              <button type="submit" className="demo-submit" disabled={submitting}>
                {submitting ? 'Sending…' : 'Book demo'}
              </button>
              <div className="demo-note">
                <h4>No Solicitation</h4>
                <p>We're always happy to connect with organizations and individuals interested in learning more about what we do. Please note that this form is intended for partnership, collaboration, and general inquiries, not unsolicited sales or promotional solicitations.</p>
              </div>
            </form>
          </Fragment>
        )}
      </div>
    </div>);

}

export {
  Announce,
  Nav,
  Hero,
  LogoStrip,
  BeyondStatic,
  Pillars,
  Capabilities,
  Showcase,
  Pricing,
  Outcomes,
  Industries,
  Testimonials,
  CSMStrip,
  TeamSection,
  CaseStudiesSection,
  FinalCTA,
  ContactSection,
  Footer,
  DemoModal,
};
